import React from "react";
import { NodeEdge } from "@hex-insights/app-modules";
import { Column, Heading, If, makeClassName, Modal, ModalProps, ViewableObserver } from "@hex-insights/core";
import {
	Announcement,
	AnnouncementFilterInput,
	AnnouncementOrderField,
	OrderDirection,
	useAnnouncementHomePageListQuery,
	useAreViewed,
} from "../../../../Utilities";
import { ChatBubble, ChatBubbleProps } from "../ChatBubble";
import styles from "./styles.module.css";

const pageSize = 20;

export type ControlledAnnouncementsListModalProps = Omit<
	AnnouncementsListModalProps,
	"isLoading" | "announcementEdges" | "viewMap" | "hasMore" | "loadMore"
> & {
	filters?: AnnouncementFilterInput;
};

export function ControlledAnnouncementsListModal({ filters, ...props }: ControlledAnnouncementsListModalProps) {
	const { loading, data, fetchMore } = useAnnouncementHomePageListQuery({
		variables: {
			filters,
			order: { field: AnnouncementOrderField.StartTime, direction: OrderDirection.Desc },
			pagination: { first: pageSize },
		},
	});
	const endCursor = data?.announcementConnection.pageInfo.endCursor;
	const loadMore = React.useCallback(() => {
		fetchMore({
			variables: { pagination: { first: pageSize, after: endCursor } },
			updateQuery: (prev, { fetchMoreResult }) => ({
				...prev,
				announcementConnection: {
					...prev.announcementConnection,
					pageInfo: fetchMoreResult.announcementConnection.pageInfo,
					edges: [...prev.announcementConnection.edges, ...fetchMoreResult.announcementConnection.edges],
				},
			}),
		});
	}, [fetchMore, endCursor]);

	const ids = React.useMemo(() => data?.announcementConnection.edges.map((e) => e.node.id) ?? [], [data]);
	const viewMap = useAreViewed(ids);

	return (
		<AnnouncementsListModal
			isLoading={loading}
			announcementEdges={data?.announcementConnection.edges}
			viewMap={viewMap}
			hasMore={data?.announcementConnection.pageInfo.hasNextPage}
			loadMore={loadMore}
			{...props}
		/>
	);
}

export type AnnouncementsListModalProps = {
	isLoading?: boolean;
	announcementEdges: NodeEdge<ChatBubbleProps["announcement"]>[] | null | undefined;
	viewMap?: Record<Announcement["id"], boolean>;
	footerContent?: React.ReactNode;
	detailModalVariant?: ChatBubbleProps["modalVariant"];
	hasMore?: boolean;
	loadMore?: () => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function AnnouncementsListModal({
	announcementEdges,
	viewMap,
	detailModalVariant,
	footerContent,
	hasMore = false,
	loadMore,
	ifRef,
	onClose,
	className,
	style,
}: AnnouncementsListModalProps) {
	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["announcements-list-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Heading.H2 noMargin>Announcements</Heading.H2>
			</Modal.Header>
			<Modal.Body className={styles["announcements-list-modal__body"]}>
				<Column justify="spaced-start">
					{announcementEdges?.map((e) => (
						<ChatBubble
							key={e.node.id}
							announcement={e.node}
							isNew={!!viewMap && !viewMap[e.node.id]}
							withModalBackButton
							modalVariant={detailModalVariant}
						/>
					))}
				</Column>
				<If condition={hasMore && !!loadMore}>
					{!!loadMore && <ViewableObserver rootMargin="0px 0px 300px" onEnterView={loadMore} />}
				</If>
			</Modal.Body>
			<If condition={!!footerContent}>
				<Modal.Footer>{footerContent}</Modal.Footer>
			</If>
		</Modal>
	);
}
